import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { dashboardService } from '../services/dashboardService'

export function useDashboardData(user = null) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Memoizar el ID del usuario para evitar re-renders innecesarios
  const userId = useMemo(() => user?.idUsuario || user?.id_usuario || null, [user?.idUsuario, user?.id_usuario])
  const userTipo = useMemo(() => user?.tipoUsuario || user?.tipo_usuario || '', [user?.tipoUsuario, user?.tipo_usuario])

  // Valores por defecto cuando no hay datos
  const defaultData = useMemo(() => ({
    resumen: {
      totalMascotas: 0,
      mascotasActivas: 0,
      totalCitas: 0,
      citasPendientes: 0,
      citasCompletadas: 0,
      totalFichas: 0,
      totalVeterinarios: 0,
      veterinariosActivos: 0,
      totalClinicas: 0,
      clinicasActivas: 0,
      totalUsuarios: 0
    },
    citasRecientes: [],
    mascotasRecientes: [],
    petSitter: null
  }), [])

  // Usar useRef para evitar múltiples ejecuciones
  const fetchingRef = useRef(false)
  const lastUserTipoRef = useRef(null)
  const hasLoadedRef = useRef(false)

  const fetchData = useCallback(async () => {
    // Evitar múltiples peticiones simultáneas
    if (fetchingRef.current) {
      return
    }

    // Solo hacer fetch si cambió el tipo de usuario o no se ha cargado aún
    if (lastUserTipoRef.current === userTipo && hasLoadedRef.current) {
      return
    }

    try {
      fetchingRef.current = true
      lastUserTipoRef.current = userTipo
      hasLoadedRef.current = false
      setLoading(true)
      setError(null)
      
      let response
      
      // Usar endpoint específico según el tipo de usuario
      if (userTipo === 'DUEÑO') {
        response = await dashboardService.getStatsDueno()
      } else if (userTipo === 'PET_SITTER') {
        response = await dashboardService.getStatsPetSitter()
      } else if (userTipo === 'VETERINARIO') {
        response = await dashboardService.getStatsVeterinario()
      } else {
        // Para ADMIN, SUPERADMIN usar endpoint general
        response = await dashboardService.getStats()
      }
      
      // El backend Laravel devuelve { success: true, data: { resumen, distribucion, crecimientoMensual, ... } }
      if (response.success && response.data) {
        // Asegurar que todos los valores numéricos tengan un valor por defecto de 0
        const processedData = {
          resumen: {
            ...defaultData.resumen,
            ...response.data.resumen
          },
          citasRecientes: response.data.citasRecientes || [],
          mascotasRecientes: response.data.mascotasRecientes || [],
          petSitter: response.data.petSitter ?? null
        }
        setData(processedData)
        hasLoadedRef.current = true
      } else {
        // Si no hay datos, usar valores por defecto
        setData(defaultData)
        hasLoadedRef.current = true
      }
    } catch (err) {
      setError(err.message || 'Error al cargar datos')
      console.error('Error al cargar datos del dashboard:', err)
      // En caso de error, usar valores por defecto
      setData(defaultData)
      hasLoadedRef.current = true
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [userTipo, defaultData]) // Incluir defaultData pero no causará re-renders porque está memoizado

  useEffect(() => {
    // Solo hacer fetch si hay usuario y tipo de usuario válido
    if (user && userTipo) {
      fetchData()
    } else if (!user) {
      // Resetear cuando no hay usuario
      setData(null)
      setLoading(false)
      lastUserTipoRef.current = null
      hasLoadedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userTipo]) // Solo cuando cambia el usuario o su tipo

  return {
    data,
    loading,
    error,
    refetch: fetchData
  }
}

